import { OptionSelected } from '../../../../inputs/DropDown/DropDownList/types';
import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

// Types
import {
  type OperatorsAndVehiclesFormValues,
  DriverList,
} from '../../../../forms/ActionForms/OperatorsAndVehicles/types';

export type NewAction = {
  operatorsTable: {
    name: string;
    surName: string;
    concession: string;
    isActive: string;
  }[];
  vehiclesTable: ({
    plate: string;
    driverID: string;
    driver: string;
    isActive: string;
  } | null)[];
};

export type TableProps = {
  data?: OperatorsAndVehiclesFormValues;
  /** Key sessionStorage de la caché de conductores del wizard actual
   *  (`${namingPersistForm}_drivers`) — por-wizard, nunca compartida. */
  driversCacheKey: string;
  operatorsData?: OperatorsSelectionType[];
  vehiclesData?: VehiclesSelectionType[];
  titles?: string[];
  control: Control<NewAction & OperatorsAndVehiclesFormValues>;
  defaultOperators: OperatorsSelectionType[];
  defaultVehicles: VehiclesSelectionType[];
  defaultDrivers: DriverList;
  tableName: string;
  setDefaultOperators: (operators: OperatorsSelectionType[]) => void;
  setDefaultVehicles: (operators: VehiclesSelectionType[]) => void;
  setValue: UseFormSetValue<OperatorsAndVehiclesFormValues>;
  setCacheDrivers?: (drivers: DriverList) => void;
  isInternal?: boolean;
  getValues: UseFormGetValues<OperatorsAndVehiclesFormValues>;
  concessions: {
    id: string;
    name: string;
    vehicleIDs: string[];
  }[];
  teams?: {
    id: string;
    name: string;
  }[];
  /** Addendum RV 2026-07-20: señal de CONTEXTO opcional para mostrar el
   *  selector activos/inactivos/todos fuera de `actions-validation` (hoy
   *  gated solo por URL vía `isValidation`). La edición del RV la pasa
   *  `true` para que un operario/vehículo inactivo ya asignado se vea. NO
   *  cambia el gating de Actuaciones: `undefined` (el default de cualquier
   *  llamador que no la pase) preserva el comportamiento actual byte a
   *  byte — el render sigue siendo `isValidation || showCrewStateFilter`. */
  showCrewStateFilter?: boolean;
};

export type OperatorsSelectionType = {
  id: string;
  name: string;
  surName: string;
  concession: string;
  isActive: string;
  team?: string; // Team name for display (legacy, consider using teamIDs)
};

export type VehiclesSelectionType = {
  driver?: string;
  vehicleID?: string;
  id: string;
  plate: string;
  driverID: string;
  remove?: string;
  color?: string;
  insurance?: string;
  isInternal?: boolean;
  isTrailer?: boolean;
  model?: string;
  policyNumber?: boolean;
  vehicleOccupantID?: string;
  isActive?: string;
  concession: string;
  teams?: string[]; // ✅ M2M: array de nombres de equipos
  teamIDs?: string[]; // ✅ M2M: array de IDs de equipos
  category?: string; // ✅ Categoría del vehículo
};

type columnIDTypes = { [key: string]: { id: string; label: string } };

// @deprecated - Use getOperatorsColumnNames and getVehiclesColumnNames instead
export const operatorsColumnNames: columnIDTypes = {
  name: {
    id: 'firstName',
    label: 'Nom',
  },
  surName: {
    id: 'lastName',
    label: 'Cognoms',
  },
  concession: {
    id: 'concession',
    label: 'Concesió',
  },
  isActive: {
    id: 'isActive',
    label: 'isActive',
  },
};

// @deprecated - Use getOperatorsColumnNames and getVehiclesColumnNames instead
export const vehiclesColumnNames: columnIDTypes = {
  plate: {
    id: 'plate',
    label: 'Matrícula',
  },
  driver: {
    id: 'driver',
    label: 'Conductor',
  },
  remove: {
    id: 'remove',
    label: '',
  },
  isActive: {
    id: 'isActive',
    label: 'isActive',
  },
  concession: {
    id: 'concession',
    label: 'Concessió',
  },
};

// Dynamic column names with translations
export const getOperatorsColumnNames = (t: any): columnIDTypes => ({
  name: {
    id: 'firstName',
    label: t('tables:headers.name'),
  },
  surName: {
    id: 'lastName',
    label: t('tables:headers.surname'),
  },
  concession: {
    id: 'concession',
    label: t('tables:headers.concession'),
  },
  isActive: {
    id: 'isActive',
    label: 'isActive',
  },
  team: {
    id: 'team',
    label: t('forms:operators.team', 'Equipo'),
  },
});

export const getVehiclesColumnNames = (t: any): columnIDTypes => ({
  plate: {
    id: 'plate',
    label: t('tables:headers.plate'),
  },
  category: {
    id: 'category',
    label: t('tables:headers.category'),
  },
  driver: {
    id: 'driver',
    label: t('tables:headers.driver'),
  },
  remove: {
    id: 'remove',
    label: '',
  },
  isActive: {
    id: 'isActive',
    label: 'isActive',
  },
  concession: {
    id: 'concession',
    label: t('tables:headers.concession'),
  },
  team: {
    id: 'team',
    label: t('forms:vehicles.team', 'Equipo'),
  },
});

export type handleChangeTypes = (value: string) => void;

export type variantLeftTh = 'select' | 'firstName' | 'lastName' | 'concession';
export type variantRightTh = 'select' | 'plate' | 'concession' | 'driver' | 'remove';

export type leftTableCellProps = {
  cell: variantLeftTh;
};

export type rightTableCellProps = {
  cell: variantRightTh;
};

export type ImperativeRefInput = {
  clearSelected: () => void;
  setSelectedOption: (option: OptionSelected | undefined) => void;
} | null;

export type DisabledType = {
  isDisabled?: boolean;
};
